import { useEffect, useState } from 'react'
import { Search } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { FilterDropdown } from '@/components/ui/filter-dropdown'
import { Input } from '@/components/ui/input'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { ApiError, getJSON } from '@/lib/api'
import type { Association } from '@/pages/StaffSignInPage'
import { PublicNotFoundPage, PublicPageShell } from './PublicPageShell'

type DirectoryField = {
  key: string
  label: string
  value: string | number | string[] | { thumbnail_url: string }
}
type DirectoryPerson = { slug: string; name: string; fields: DirectoryField[] }
export type PublicDirectoryResponse = {
  member_directory: {
    people: DirectoryPerson[]
    page: number
    page_size: number
    total: number
    filters: Array<{ key: string; label: string; choices: string[] }>
  }
}

export function PublicMemberDirectoryPage({ association }: { association: Association }) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [selectedFilters, setSelectedFilters] = useState<Record<string, Set<string>>>({})
  const filterKey = selectedFilterKey(selectedFilters)
  const query = useQuery<PublicDirectoryResponse>({
    queryKey: ['public-member-directory', search, page, filterKey],
    queryFn: () => getJSON(publicDirectoryPath({ search, page, selectedFilters })),
    retry: false,
  })

  useEffect(() => setPage(1), [search, filterKey])

  if (query.isError && query.error instanceof ApiError && query.error.status === 404) {
    return <PublicNotFoundPage association={association} />
  }
  if (query.isLoading) {
    return (
      <PublicPageShell association={association}>
        <p className="py-16 text-body-sm text-muted-foreground" role="status">
          Loading directory…
        </p>
      </PublicPageShell>
    )
  }
  if (query.isError || !query.data) {
    return (
      <PublicPageShell association={association}>
        <Alert>Directory unavailable.</Alert>
      </PublicPageShell>
    )
  }

  const directory = query.data.member_directory
  const hasFilters =
    search.trim() !== '' || Object.values(selectedFilters).some((values) => values.size > 0)
  const pages = Math.max(1, Math.ceil(directory.total / directory.page_size))
  const clearFilters = () => {
    setSearch('')
    setSelectedFilters({})
  }

  return (
    <PublicPageShell association={association}>
      <div className="relative isolate">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-64 -top-64 -z-10 size-[48rem] rounded-full bg-[image:var(--gradient-glow)] blur-[120px]"
        />
        <div className="relative z-10 mb-6 space-y-1 py-8">
          <h1 className="text-heading font-semibold tracking-tight">{association.name}</h1>
          <p className="text-caption text-muted-foreground">Member directory</p>
        </div>
        <div className="relative z-10 space-y-6">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-0 max-w-sm flex-1">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                aria-label="Search public directory"
                className="h-8 pl-8"
                placeholder="Search name or organization"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            {directory.filters.map((filter) => (
              <FilterDropdown
                key={filter.key}
                label={filter.label}
                emptyLabel={filter.label}
                options={filter.choices.map((choice) => ({ value: choice, label: choice }))}
                selected={selectedFilters[filter.key] ?? new Set()}
                onChange={(values) =>
                  setSelectedFilters((current) => ({ ...current, [filter.key]: values }))
                }
              />
            ))}
          </div>
          <div className="flex items-center justify-between">
            <p className="text-caption text-muted-foreground">
              {directory.total} member{directory.total === 1 ? '' : 's'}
            </p>
            <p className="text-caption text-muted-foreground">Sorted by last name</p>
          </div>
          {directory.total === 0 ? (
            <EmptyState
              title={hasFilters ? 'No members match your search.' : 'No members are listed yet.'}
              action={
                hasFilters ? (
                  <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
                    Clear filters
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {directory.people.map((person) => (
                  <DirectoryCard key={person.slug} person={person} />
                ))}
              </div>
              {pages > 1 && (
                <DirectoryPagination page={directory.page} pages={pages} onPageChange={setPage} />
              )}
            </>
          )}
        </div>
      </div>
    </PublicPageShell>
  )
}

function DirectoryCard({ person }: { person: DirectoryPerson }) {
  const photo = person.fields.find((field) => field.key === 'profile_photo')?.value
  const level = person.fields.find((field) => field.key === 'membership_level')?.value
  const organizations = person.fields.find(
    (field) => field.key === 'organization_affiliations',
  )?.value
  const photoUrl =
    typeof photo === 'object' && photo !== null && !Array.isArray(photo)
      ? photo.thumbnail_url
      : null
  const organizationText = Array.isArray(organizations) ? organizations.join(', ') : organizations
  return (
    <a
      href={`/directory/${person.slug}`}
      className="block overflow-hidden rounded-md border border-border bg-card transition-colors hover:border-primary/40"
    >
      {photoUrl ? (
        <img src={photoUrl} alt="" className="aspect-square w-full object-cover" />
      ) : (
        <div className="flex aspect-square w-full items-center justify-center bg-muted">
          <span className="text-heading-sm font-medium text-muted-foreground">
            {initials(person.name)}
          </span>
        </div>
      )}
      <div className="space-y-0.5 p-3 leading-snug">
        {level !== undefined && (
          <p className="text-caption font-medium text-primary">{String(level)}</p>
        )}
        <p className="truncate text-body-sm font-medium">{person.name}</p>
        {organizationText && (
          <p className="truncate text-caption text-muted-foreground">{String(organizationText)}</p>
        )}
      </div>
    </a>
  )
}

function DirectoryPagination({
  page,
  pages,
  onPageChange,
}: {
  page: number
  pages: number
  onPageChange: (page: number) => void
}) {
  const visiblePages = paginationPages(page, pages)
  return (
    <Pagination>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#"
            aria-disabled={page === 1}
            className={page === 1 ? 'pointer-events-none opacity-50' : undefined}
            onClick={(event) => {
              event.preventDefault()
              if (page > 1) onPageChange(page - 1)
            }}
          />
        </PaginationItem>
        {visiblePages.map((number, index) =>
          number === null ? (
            <PaginationItem key={`ellipsis-${index}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={number}>
              <PaginationLink
                href="#"
                isActive={number === page}
                onClick={(event) => {
                  event.preventDefault()
                  onPageChange(number)
                }}
              >
                {number}
              </PaginationLink>
            </PaginationItem>
          ),
        )}
        <PaginationItem>
          <PaginationNext
            href="#"
            aria-disabled={page === pages}
            className={page === pages ? 'pointer-events-none opacity-50' : undefined}
            onClick={(event) => {
              event.preventDefault()
              if (page < pages) onPageChange(page + 1)
            }}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
}

function publicDirectoryPath({
  search,
  page,
  selectedFilters,
}: {
  search: string
  page: number
  selectedFilters: Record<string, Set<string>>
}) {
  const params = new URLSearchParams({ page: String(page) })
  if (search.trim()) params.set('search', search.trim())
  Object.entries(selectedFilters).forEach(([key, values]) =>
    values.forEach((value) => params.append(`filters[${key}]`, value)),
  )
  return `/api/v1/public_member_directory?${params.toString()}`
}

function selectedFilterKey(filters: Record<string, Set<string>>) {
  return Object.entries(filters)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, values]) => `${key}:${Array.from(values).sort().join(',')}`)
    .join('|')
}
function paginationPages(page: number, pages: number): Array<number | null> {
  if (pages <= 7) return Array.from({ length: pages }, (_, index) => index + 1)
  const start = Math.max(2, Math.min(page - 1, pages - 3))
  const end = Math.min(pages - 1, start + 2)
  return [
    1,
    ...(start > 2 ? [null] : []),
    ...Array.from({ length: end - start + 1 }, (_, index) => start + index),
    ...(end < pages - 1 ? [null] : []),
    pages,
  ]
}
function initials(name: string) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}
